import { useState, useRef } from 'react';
import { useBulkImportOAuthConfigs } from '../../hooks/useOAuthConfigs';
import type { BulkImportConfig, OAuthProvider } from '../../types/oauth';
import { OAUTH_PROVIDERS, PROVIDER_DISPLAY_NAMES, DEFAULT_SCOPES } from '../../types/oauth';
import './OAuthBulkImport.css';

interface OAuthBulkImportProps {
  projectId: string;
  onClose: () => void;
  onSuccess: () => void;
}

interface ImportEntry extends BulkImportConfig {
  id: string;
  isValid: boolean;
  errors: string[];
}

export function OAuthBulkImport({ projectId, onClose, onSuccess }: OAuthBulkImportProps) {
  const [entries, setEntries] = useState<ImportEntry[]>([]);
  const [jsonInput, setJsonInput] = useState('');
  const [parseError, setParseError] = useState<string | null>(null);
  const [importMode, setImportMode] = useState<'form' | 'json'>('form');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const bulkImport = useBulkImportOAuthConfigs();

  const addEntry = () => {
    setEntries([
      ...entries,
      {
        id: crypto.randomUUID(),
        provider: 'google' as OAuthProvider,
        client_id: '',
        client_secret: '',
        scopes: DEFAULT_SCOPES.google,
        isValid: false,
        errors: ['Client ID is required', 'Client Secret is required'],
      },
    ]);
  };

  const removeEntry = (id: string) => {
    setEntries(entries.filter((e) => e.id !== id));
  };

  const updateEntry = (id: string, updates: Partial<ImportEntry>) => {
    setEntries(
      entries.map((e) => {
        if (e.id !== id) return e;

        const updated = { ...e, ...updates };

        // Validate
        const errors: string[] = [];
        if (!updated.client_id?.trim()) errors.push('Client ID is required');
        if (!updated.client_secret?.trim()) errors.push('Client Secret is required');
        if (updated.provider === 'okta' && !updated.provider_settings?.issuer) {
          errors.push('Okta Issuer URL is required');
        }

        return {
          ...updated,
          isValid: errors.length === 0,
          errors,
        };
      })
    );
  };

  const handleProviderChange = (id: string, provider: OAuthProvider) => {
    updateEntry(id, {
      provider,
      scopes: DEFAULT_SCOPES[provider],
      provider_settings: provider === 'okta' ? { issuer: '' } : undefined,
    });
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      setJsonInput(content);
      parseJsonInput(content);
    };
    reader.readAsText(file);
  };

  const parseJsonInput = (input: string) => {
    setParseError(null);

    try {
      const parsed = JSON.parse(input);
      const configs = Array.isArray(parsed) ? parsed : parsed.configs || [parsed];

      const newEntries: ImportEntry[] = configs.map((config: BulkImportConfig) => {
        const errors: string[] = [];

        if (!config.provider || !OAUTH_PROVIDERS.includes(config.provider)) {
          errors.push('Invalid or missing provider');
        }
        if (!config.client_id?.trim()) errors.push('Client ID is required');
        if (!config.client_secret?.trim()) errors.push('Client Secret is required');
        if (config.provider === 'okta' && !config.provider_settings?.issuer) {
          errors.push('Okta Issuer URL is required');
        }

        return {
          id: crypto.randomUUID(),
          provider: config.provider || 'google',
          client_id: config.client_id || '',
          client_secret: config.client_secret || '',
          scopes: config.scopes || DEFAULT_SCOPES[config.provider] || [],
          provider_settings: config.provider_settings,
          isValid: errors.length === 0,
          errors,
        };
      });

      setEntries(newEntries);
    } catch (err) {
      setParseError('Invalid JSON format. Please check your input.');
    }
  };

  const handleImport = async () => {
    const validConfigs: BulkImportConfig[] = entries
      .filter((e) => e.isValid)
      .map(({ provider, client_id, client_secret, scopes, provider_settings }) => ({
        provider,
        client_id,
        client_secret,
        scopes,
        provider_settings,
      }));

    if (validConfigs.length === 0) return;

    try {
      const result = await bulkImport.mutateAsync({
        projectId,
        configs: validConfigs,
      });

      if (result.failed === 0) {
        onSuccess();
      }
    } catch {
      // Error handled by mutation
    }
  };

  const validCount = entries.filter((e) => e.isValid).length;
  const canImport = validCount > 0 && !bulkImport.isPending;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal--bulk-import" onClick={(e) => e.stopPropagation()}>
        <header className="modal__header">
          <h2>Bulk Import OAuth Providers</h2>
          <button className="modal__close" onClick={onClose} type="button">
            &times;
          </button>
        </header>

        <div className="modal__body">
          {/* Import Mode Tabs */}
          <div className="import-mode-tabs">
            <button
              type="button"
              className={`import-mode-tab ${importMode === 'form' ? 'import-mode-tab--active' : ''}`}
              onClick={() => setImportMode('form')}
            >
              Manual Entry
            </button>
            <button
              type="button"
              className={`import-mode-tab ${importMode === 'json' ? 'import-mode-tab--active' : ''}`}
              onClick={() => setImportMode('json')}
            >
              JSON Import
            </button>
          </div>

          {importMode === 'json' && (
            <div className="json-import-section">
              <div className="json-import-actions">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json"
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                />
                <button
                  type="button"
                  className="btn btn--secondary btn--sm"
                  onClick={() => fileInputRef.current?.click()}
                >
                  Upload JSON File
                </button>
                <span className="text-muted">or paste JSON below</span>
              </div>

              <textarea
                className="json-input"
                value={jsonInput}
                onChange={(e) => setJsonInput(e.target.value)}
                placeholder={`[
  {
    "provider": "google",
    "client_id": "your-client-id",
    "client_secret": "your-client-secret"
  },
  {
    "provider": "github",
    "client_id": "your-github-client-id",
    "client_secret": "your-github-secret"
  }
]`}
                rows={8}
              />

              {parseError && <p className="json-error">{parseError}</p>}

              <button
                type="button"
                className="btn btn--secondary"
                onClick={() => parseJsonInput(jsonInput)}
                disabled={!jsonInput.trim()}
              >
                Parse JSON
              </button>
            </div>
          )}

          {importMode === 'form' && (
            <div className="form-import-section">
              <button
                type="button"
                className="btn btn--secondary"
                onClick={addEntry}
              >
                + Add Provider
              </button>
            </div>
          )}

          {/* Entries List */}
          {entries.length > 0 && (
            <div className="import-entries">
              <h4>Providers to Import ({validCount}/{entries.length} valid)</h4>

              {entries.map((entry, index) => (
                <div
                  key={entry.id}
                  className={`import-entry ${entry.isValid ? 'import-entry--valid' : 'import-entry--invalid'}`}
                >
                  <div className="import-entry__header">
                    <span className="import-entry__number">{index + 1}</span>
                    <select
                      value={entry.provider}
                      onChange={(e) => handleProviderChange(entry.id, e.target.value as OAuthProvider)}
                      className="import-entry__provider-select"
                    >
                      {OAUTH_PROVIDERS.map((p) => (
                        <option key={p} value={p}>
                          {PROVIDER_DISPLAY_NAMES[p]}
                        </option>
                      ))}
                    </select>
                    <span className={`import-entry__status ${entry.isValid ? 'text-success' : 'text-error'}`}>
                      {entry.isValid ? 'Valid' : 'Invalid'}
                    </span>
                    <button
                      type="button"
                      className="import-entry__remove"
                      onClick={() => removeEntry(entry.id)}
                      aria-label="Remove"
                    >
                      &times;
                    </button>
                  </div>

                  <div className="import-entry__fields">
                    <div className="import-entry__field">
                      <label>Client ID</label>
                      <input
                        type="text"
                        value={entry.client_id}
                        onChange={(e) => updateEntry(entry.id, { client_id: e.target.value })}
                        placeholder="Enter client ID"
                      />
                    </div>
                    <div className="import-entry__field">
                      <label>Client Secret</label>
                      <input
                        type="password"
                        value={entry.client_secret}
                        onChange={(e) => updateEntry(entry.id, { client_secret: e.target.value })}
                        placeholder="Enter client secret"
                      />
                    </div>
                    {entry.provider === 'okta' && (
                      <div className="import-entry__field">
                        <label>Okta Issuer URL</label>
                        <input
                          type="url"
                          value={(entry.provider_settings?.issuer as string) || ''}
                          onChange={(e) =>
                            updateEntry(entry.id, {
                              provider_settings: { ...entry.provider_settings, issuer: e.target.value },
                            })
                          }
                          placeholder="https://dev-123456.okta.com"
                        />
                      </div>
                    )}
                  </div>

                  {entry.errors.length > 0 && (
                    <ul className="import-entry__errors">
                      {entry.errors.map((error, i) => (
                        <li key={i}>{error}</li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Import Results */}
          {bulkImport.data && (
            <div className="import-results">
              <h4>Import Results</h4>
              <p>
                <span className="text-success">{bulkImport.data.created} created</span>
                {bulkImport.data.failed > 0 && (
                  <span className="text-error"> • {bulkImport.data.failed} failed</span>
                )}
              </p>
              {bulkImport.data.results.some((r) => r.status === 'failed') && (
                <ul className="import-results__errors">
                  {bulkImport.data.results
                    .filter((r) => r.status === 'failed')
                    .map((r, i) => (
                      <li key={i}>
                        {PROVIDER_DISPLAY_NAMES[r.provider]}: {r.error}
                      </li>
                    ))}
                </ul>
              )}
            </div>
          )}

          {bulkImport.isError && (
            <div className="import-error">
              <p>{bulkImport.error?.message || 'Import failed'}</p>
            </div>
          )}
        </div>

        <footer className="modal__footer">
          <button
            type="button"
            className="btn btn--secondary"
            onClick={onClose}
            disabled={bulkImport.isPending}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={handleImport}
            disabled={!canImport}
          >
            {bulkImport.isPending
              ? 'Importing...'
              : `Import ${validCount} Provider${validCount !== 1 ? 's' : ''}`}
          </button>
        </footer>
      </div>
    </div>
  );
}
